import React, { type FC, useEffect, useRef, useState } from 'react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import useGetAuctionIds from '../../hooks/api/ListSort/useGetAuctionIds';
import { AuctionIdOption } from '../../api/ListSort/getAuctionIds';

type CustomListIdLookupProps = {
  listSortId: string;
  onSelect: (option: AuctionIdOption) => void;
  isJumping?: boolean;
  message?: string | null;
};

// Type-ahead lookup: the user can type a Custom List ID directly or open the list
// and pick one. Suggestions update as they type.
const CustomListIdLookup: FC<CustomListIdLookupProps> = ({ listSortId, onSelect, isJumping = false, message = null }) => {
  const [ inputValue, setInputValue ] = useState('');
  const [ isOpen, setIsOpen ] = useState(false);
  const [ highlightedIndex, setHighlightedIndex ] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const { auctionIds, isLoadingAuctionIds } = useGetAuctionIds(listSortId, inputValue, isOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [ auctionIds ]);

  const selectOption = (option: AuctionIdOption) => {
    setInputValue(option.auction_number);
    setIsOpen(false);
    onSelect(option);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((index) => Math.min(index + 1, auctionIds.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const typed = inputValue.trim().toLowerCase();
      const exactMatch = auctionIds.find((option) => option.auction_number.toLowerCase() === typed);
      const choice = auctionIds[highlightedIndex] ?? exactMatch ?? (auctionIds.length === 1 ? auctionIds[0] : undefined);
      if (choice) {
        selectOption(choice);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showNoMatches = isOpen && !isLoadingAuctionIds && inputValue.trim() !== '' && auctionIds.length === 0;

  return (
    <div id='custom-list-id-lookup' ref={containerRef} className='flex items-center'>
      <label htmlFor='custom-list-id-lookup-input' className='whitespace-nowrap block text-base text-gray-700 pr-3'>
        Find Custom List ID:
      </label>
      <div className='relative'>
        <input
          id='custom-list-id-lookup-input'
          data-tag='custom-list-id-lookup-input'
          type='text'
          role='combobox'
          aria-expanded={isOpen}
          aria-controls='custom-list-id-lookup-options'
          aria-autocomplete='list'
          autoComplete='off'
          placeholder='Type or select an ID'
          className='w-48 rounded-md border border-gray-300 bg-white pl-3 pr-10 py-2 text-left focus:outline-none focus:border-indigo-300 sm:text-sm sm:leading-5'
          value={inputValue}
          disabled={isJumping}
          onChange={(event) => {
            setInputValue(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <button
          type='button'
          aria-label='Show Custom List IDs'
          tabIndex={-1}
          className='absolute inset-y-0 right-0 flex items-center pr-2'
          onClick={() => setIsOpen((open) => !open)}
        >
          <ChevronUpDownIcon className='h-5 w-5 text-gray-400' />
        </button>
        {isOpen && (auctionIds.length > 0 || showNoMatches) && (
          <ul
            id='custom-list-id-lookup-options'
            role='listbox'
            className='absolute bg-white max-h-60 w-full rounded-md py-1 text-base leading-6 shadow-xs overflow-auto border z-10 sm:text-sm sm:leading-5'
          >
            {auctionIds.map((option, index) => (
              <li
                key={`${option.property_id}-${option.auction_number}`}
                role='option'
                aria-selected={index === highlightedIndex}
                className={`${index === highlightedIndex ? 'text-white bg-indigo-600' : 'text-gray-900'} cursor-default select-none relative py-2 px-4`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectOption(option);
                }}
              >
                {option.auction_number}
              </li>
            ))}
            {showNoMatches && <li className='py-2 px-4 text-gray-500 cursor-default'>No matching Custom List IDs</li>}
          </ul>
        )}
      </div>
      {(isJumping || message) && (
        <span className={`pl-3 text-sm ${message ? 'text-red-600' : 'text-gray-500'}`}>
          {message ?? 'Loading...'}
        </span>
      )}
    </div>
  );
};

export default CustomListIdLookup;
